/**
 * Avatar profiles.
 *
 * An avatar is described by a JSON manifest sitting next to its model, not by
 * anything compiled into the bundle. Swapping the guide for a different
 * character — with different clip names, different blend shapes, a different
 * rig entirely — is then dropping a folder into `public/avatars/` and pointing
 * at it, with no code change and no rebuild.
 *
 * Nothing here is required. Every field has a default, and any clip or morph
 * target the model does not have is reported and skipped rather than throwing,
 * so a static model with no animation at all still works — it simply stands
 * there. Silence is the enemy: unresolved names are logged, because a mistyped
 * clip otherwise just means an animation that mysteriously never plays.
 */
import { uiLog } from '../ui/uiLog.js';

/** Coarse mood the backend can send when it does not name an exact clip. */
export type GestureHint = 'idle' | 'thinking' | 'speaking';

/** One directional light, described by where its rays travel. */
export interface DirectionalLightSpec {
  /**
   * Direction of travel in **avatar-local** space, so it tracks the guide as it
   * turns to face the viewer. The guide's +Z faces the viewer, so `[0, 0, -1]`
   * is a flat frontal light and lowering Y brings it down from above.
   */
  direction: [number, number, number];
  /** sRGB hex. */
  color: string;
  intensity: number;
}

/**
 * How the guide is lit.
 *
 * Passthrough gives you a real room and no photons: IWSDK adds **no** default
 * light and no default environment, so a PBR model with nothing here renders
 * pure black. Every avatar carries its own rig because exposure that flatters a
 * stylised character blows out a photoreal one.
 */
export interface AvatarLighting {
  /** Main light. Shapes the face, so it is worth aiming. */
  key: DirectionalLightSpec;
  /** Opposite side at lower intensity, to keep shadow detail off pure black. */
  fill: DirectionalLightSpec;
  /** Sky/ground wash. World-oriented — it ignores the guide's facing. */
  ambient: { skyColor: string; groundColor: string; intensity: number };
  /**
   * Image-based lighting. `'room'` is three's neutral studio box, generated in
   * memory with no asset to ship; `'none'` disables IBL. Anything else is
   * treated as a URL to an equirectangular `.hdr`/`.exr`/image.
   *
   * Without this, metal and smooth surfaces have nothing to reflect and read as
   * flat plastic — lights alone illuminate but do not make PBR look right.
   */
  environment: string;
  environmentIntensity: number;
}

export interface AvatarProfile {
  /** Folder name under `public/avatars/`, filled in by the loader. */
  id: string;
  /** Human-readable, for logs. */
  name: string;
  /** Model file, resolved relative to the profile folder. */
  model: string;

  /** Normalised standing height in metres, measured from the model's bounds. */
  targetHeightMeters: number;
  /** Seconds the fade-in takes on placement. */
  entranceSeconds: number;
  /** Cross-fade between clips, in seconds. */
  transitionSeconds: number;

  clips: {
    /** Looped when nothing else is playing. */
    idle: string;
    /** Played once on arrival. */
    greeting: string;
    /** Played while a reply is in flight. */
    thinking: string;
    /** Occasional variations on the idle loop. */
    idlePool: string[];
    /** Fallback clip per hint. Empty string means "no gesture". */
    byHint: Record<GestureHint, string>;
    /** Seconds between idle variations, picked uniformly in this range. */
    idleIntervalRange: [number, number];
  };

  morphs: {
    /** Driven together while the guide speaks. Empty disables mouth movement. */
    mouth: string[];
    /** Driven by the blink timer. Several for rigs that split left/right. */
    blink: string[];
    /** Peak mouth-open influence (0..1). */
    mouthIntensity: number;
    /** Seconds between blinks, picked uniformly in this range. */
    blinkIntervalRange: [number, number];
  };

  lighting: AvatarLighting;
}

/** Everything optional, so a manifest can be as small as `{}`. */
export type AvatarProfileInput = DeepPartial<Omit<AvatarProfile, 'id'>>;

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends readonly unknown[] ? T[K] : T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export const DEFAULT_PROFILE: Omit<AvatarProfile, 'id'> = {
  name: 'Guide',
  model: 'model.glb',
  targetHeightMeters: 1.1,
  entranceSeconds: 1.2,
  transitionSeconds: 0.3,
  clips: {
    idle: 'Idle',
    greeting: '',
    thinking: '',
    idlePool: [],
    byHint: { idle: '', thinking: '', speaking: '' },
    idleIntervalRange: [8, 15],
  },
  morphs: {
    mouth: [],
    blink: [],
    mouthIntensity: 0.6,
    blinkIntervalRange: [2.5, 6],
  },
  lighting: {
    // Down from above, over the viewer's right shoulder — the standard
    // three-quarter key. Straight-on (`[0, 0, -1]`) flattens a face completely.
    key: { direction: [-0.45, -0.75, -0.62], color: '#fff4e6', intensity: 2.4 },
    // Cool and shallow from the other side, so the shadow side reads as shadow
    // rather than as a hole.
    fill: { direction: [0.7, -0.15, -0.6], color: '#c8dcff', intensity: 0.8 },
    ambient: { skyColor: '#c9d8ea', groundColor: '#6b6255', intensity: 1.1 },
    environment: 'room',
    environmentIntensity: 0.75,
  },
};

/**
 * Which avatar to load, in priority order:
 *   1. `?avatar=<id>` — a per-open override, so alternatives can be compared
 *      on-device without a redeploy.
 *   2. `VITE_AVATAR_ID` — baked in at build time.
 *   3. `default` — the folder shipped with the app.
 */
export function resolveAvatarId(search: string = window.location.search): string {
  const override = new URLSearchParams(search).get('avatar');
  // Constrain to a path segment: this becomes part of a fetch URL.
  if (override && /^[a-z0-9][a-z0-9._-]*$/i.test(override)) return override;

  const fromEnv = import.meta.env.VITE_AVATAR_ID;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;

  return 'default';
}

export function profileBaseUrl(id: string): string {
  return `/avatars/${id}`;
}

/** Fetch and normalise a profile. Falls back to defaults if the fetch fails. */
export async function loadProfile(id: string): Promise<AvatarProfile> {
  const base = profileBaseUrl(id);
  try {
    const response = await fetch(`${base}/avatar.json`, { cache: 'no-cache' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const raw: unknown = await response.json();
    return mergeProfile(id, raw as AvatarProfileInput);
  } catch (error) {
    uiLog.push(`avatar profile "${id}" unavailable (${describe(error)}) — using defaults`);
    return mergeProfile(id, {});
  }
}

/** Merge a partial manifest over the defaults. Exported for tests. */
export function mergeProfile(id: string, input: AvatarProfileInput): AvatarProfile {
  const d = DEFAULT_PROFILE;
  const clips = input.clips ?? {};
  const morphs = input.morphs ?? {};
  const lighting = input.lighting ?? {};

  return {
    id,
    name: str(input.name, d.name),
    model: str(input.model, d.model),
    targetHeightMeters: num(input.targetHeightMeters, d.targetHeightMeters, 0.05, 10),
    entranceSeconds: num(input.entranceSeconds, d.entranceSeconds, 0, 30),
    transitionSeconds: num(input.transitionSeconds, d.transitionSeconds, 0, 5),
    clips: {
      idle: str(clips.idle, d.clips.idle),
      greeting: str(clips.greeting, d.clips.greeting),
      thinking: str(clips.thinking, d.clips.thinking),
      idlePool: strArray(clips.idlePool, d.clips.idlePool),
      byHint: {
        idle: str(clips.byHint?.idle, d.clips.byHint.idle),
        thinking: str(clips.byHint?.thinking, d.clips.byHint.thinking),
        speaking: str(clips.byHint?.speaking, d.clips.byHint.speaking),
      },
      idleIntervalRange: range(clips.idleIntervalRange, d.clips.idleIntervalRange),
    },
    morphs: {
      mouth: strArray(morphs.mouth, d.morphs.mouth),
      blink: strArray(morphs.blink, d.morphs.blink),
      mouthIntensity: num(morphs.mouthIntensity, d.morphs.mouthIntensity, 0, 1),
      blinkIntervalRange: range(morphs.blinkIntervalRange, d.morphs.blinkIntervalRange),
    },
    lighting: {
      key: light(lighting.key, d.lighting.key),
      fill: light(lighting.fill, d.lighting.fill),
      ambient: {
        skyColor: hex(lighting.ambient?.skyColor, d.lighting.ambient.skyColor),
        groundColor: hex(lighting.ambient?.groundColor, d.lighting.ambient.groundColor),
        intensity: num(lighting.ambient?.intensity, d.lighting.ambient.intensity, 0, 20),
      },
      environment: str(lighting.environment, d.lighting.environment),
      environmentIntensity: num(
        lighting.environmentIntensity,
        d.lighting.environmentIntensity,
        0,
        10,
      ),
    },
  };
}

/**
 * Report which configured names the model actually has.
 *
 * Called after load so a typo surfaces as a log line rather than an animation
 * that silently never plays — by far the most common way this breaks.
 */
export function reportProfileCoverage(
  profile: AvatarProfile,
  available: { clips: Set<string>; morphs: Set<string> },
): void {
  const missingClips = uniqueNames([
    profile.clips.idle,
    profile.clips.greeting,
    profile.clips.thinking,
    ...profile.clips.idlePool,
    ...Object.values(profile.clips.byHint),
  ]).filter((name) => !available.clips.has(name));

  const missingMorphs = uniqueNames([...profile.morphs.mouth, ...profile.morphs.blink]).filter(
    (name) => !available.morphs.has(name),
  );

  if (missingClips.length > 0) {
    uiLog.push(`avatar "${profile.id}": missing clips — ${missingClips.join(', ')}`);
  }
  if (missingMorphs.length > 0) {
    uiLog.push(`avatar "${profile.id}": missing morph targets — ${missingMorphs.join(', ')}`);
  }
  if (available.clips.size === 0) {
    uiLog.push(`avatar "${profile.id}": model has no animations — the guide will stand still`);
  }
}

const uniqueNames = (names: string[]): string[] => [...new Set(names.filter((n) => n !== ''))];

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function num(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(Math.max(value, min), max);
}

function strArray(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return [...fallback];
  return value.filter((v): v is string => typeof v === 'string' && v !== '');
}

/** Accepts `#rgb` and `#rrggbb`; anything else falls back. */
function hex(value: unknown, fallback: string): string {
  return typeof value === 'string' && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)
    ? value
    : fallback;
}

function light(value: unknown, fallback: DirectionalLightSpec): DirectionalLightSpec {
  const input = (value ?? {}) as Partial<DirectionalLightSpec>;
  return {
    direction: vec3(input.direction, fallback.direction),
    color: hex(input.color, fallback.color),
    // Generous ceiling: a dark model legitimately needs a hot key, and the only
    // thing over-lighting costs is a blown-out guide.
    intensity: num(input.intensity, fallback.intensity, 0, 50),
  };
}

/** A zero-length direction would leave the light aiming nowhere, so it falls back. */
function vec3(
  value: unknown,
  fallback: [number, number, number],
): [number, number, number] {
  if (!Array.isArray(value) || value.length !== 3) return [...fallback];
  const [x, y, z] = value;
  if (![x, y, z].every((n) => typeof n === 'number' && Number.isFinite(n))) return [...fallback];
  if (x * x + y * y + z * z < 1e-9) return [...fallback];
  return [x, y, z] as [number, number, number];
}

function range(value: unknown, fallback: [number, number]): [number, number] {
  if (!Array.isArray(value) || value.length !== 2) return [...fallback];
  const [a, b] = value;
  if (typeof a !== 'number' || typeof b !== 'number' || !Number.isFinite(a) || !Number.isFinite(b)) {
    return [...fallback];
  }
  // Tolerate a reversed range rather than producing negative intervals.
  return a <= b ? [a, b] : [b, a];
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
